import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EnvelopeSimple, ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/auth/forgot-password", { email });
            setSent(true);
            toast.success("Request received");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };


    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-[#F8FAFC]">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="flex items-center gap-2.5 mb-10">
                    <div className="h-10 w-10 flex items-center justify-center">
                        <Logo size={40} />
                    </div>
                    <div className="leading-tight">
                        <div className="font-bold text-base text-[#003366]" style={{ fontFamily: "Outfit" }}>
                            ADVANCE NETWORKS
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            Technician Portal
                        </div>
                    </div>
                </div>

                <div className="mb-8">
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">
                        Account recovery
                    </div>
                    <h2
                        className="text-3xl sm:text-4xl font-bold text-[#003366]"
                        style={{ fontFamily: "Outfit" }}
                        data-testid="forgot-password-heading"
                    >
                        Forgot your password?
                    </h2>
                    <p className="text-sm text-slate-600 mt-3 leading-relaxed">
                        Enter the email tied to your account and we'll notify your administrator, who
                        will send you a secure link to set a new password.
                    </p>
                </div>

                {!sent ? (
                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-password-form">
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Email address
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                placeholder="you@advance-net.co"
                                className="h-11 border-[#E2E8F0] focus-visible:ring-[#0066D9]"
                                data-testid="forgot-password-email-input"
                            />
                        </div>

                        <Button
                            type="submit"
                            disabled={submitting}
                            className="w-full h-11 bg-[#0066D9] hover:bg-[#0052ad] text-white font-medium"
                            data-testid="forgot-password-submit-button"
                        >
                            {submitting ? "Sending..." : (<><EnvelopeSimple size={18} className="mr-2" /> Request a reset link</>)}
                        </Button>
                    </form>
                ) : (
                    <div className="space-y-5" data-testid="forgot-password-success">
                        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 flex gap-3">
                            <CheckCircle size={22} weight="fill" className="text-emerald-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-emerald-900 leading-relaxed">
                                Recovery request received for <span className="font-semibold">{email}</span>.
                                An administrator will send you a single-use reset link — it expires 30
                                minutes after they create it. Contact your supervisor if you need it urgently.
                            </div>
                        </div>


                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setSent(false)}
                            className="w-full h-11"
                            data-testid="forgot-password-resend-button"
                        >
                            Send to a different email
                        </Button>
                    </div>
                )}

                <div className="mt-8 pt-6 border-t border-[#E2E8F0] text-sm text-slate-600">
                    <Link
                        to="/login"
                        className="inline-flex items-center text-[#0066D9] font-semibold hover:underline"
                        data-testid="link-back-to-login"
                    >
                        <ArrowLeft size={16} className="mr-1.5" /> Back to sign in
                    </Link>
                </div>
            </div>
        </div>
    );
}
